import React, { useContext, useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  IconButton,
  Typography,
  Box,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SaveIcon from '@mui/icons-material/Save';
import { useForm, FormProvider } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import type { AddMaterialModalProps, AddMaterialFormValues } from './types';
import { addMaterialSchema, DEFAULT_FORM_VALUES } from './validation';
import { QuantitySpecForm } from './QuantitySpecForm';
import { ContainerSpecForm } from './ContainerSpecForm';
import { ChooseWellAddressDialog } from './ChooseWellAddressDialog';
import { useGraphEditorStore } from '@/graph-store';
import type { MaterialNodeSpec, Container, Unit } from '@/graph/domain/types';
import { ToastContext } from '@/context/ToastContext';

function formToSpec(
  data: AddMaterialFormValues,
  position?: { x: number; y: number },
): MaterialNodeSpec {
  const materialName = data.name.trim();
  const isPlate = data.containerType === 'Well Plate';

  const containerVolume =
    !isPlate && data.containerVolumeAmount > 0
      ? {
          amount: data.containerVolumeAmount,
          unit: data.containerVolumeUnits as Unit,
        }
      : undefined;

  const container: Container = {
    id: '',
    name: isPlate
      ? data.plateName
      : data.sameAsMaterial
        ? data.name
        : data.containerName,
    containerType: data.containerType,
    volume: containerVolume,
    make: data.vendor || undefined,
    copyName: !isPlate && data.sameAsMaterial,
    rows: isPlate ? data.rows : undefined,
    columns: isPlate ? data.columns : undefined,
    wells: isPlate ? data.rows * data.columns : undefined,
    wellVolume:
      isPlate && data.wellVolumeAmount > 0
        ? {
            amount: data.wellVolumeAmount,
            units: data.wellVolumeUnits as Unit,
          }
        : undefined,
    wellAddresses:
      isPlate && data.wellAddresses.length > 0
        ? data.wellAddresses
        : undefined,
  };

  return {
    name: materialName,
    container,
    constituents:
      data.isEmpty || materialName.length === 0
        ? []
        : [{ name: materialName, weight: 1 }],
    quantity: {
      value: {
        amount: data.isEmpty ? 0 : data.amount,
        units: data.units as Unit,
      },
      type: data.quantityType,
    },
    isEmpty: data.isEmpty,
    position,
  };
}

export function AddMaterialModal({
  position,
  onClose,
  onSaved,
}: AddMaterialModalProps) {
  const addMaterialNode = useGraphEditorStore((s) => s.addMaterialNode);
  const [wellDialogOpen, setWellDialogOpen] = useState(false);
  const toast = useContext(ToastContext);

  const methods = useForm<AddMaterialFormValues>({
    resolver: zodResolver(addMaterialSchema) as never,
    defaultValues: DEFAULT_FORM_VALUES,
    mode: 'onChange',
    reValidateMode: 'onChange',
  });

  const {
    handleSubmit,
    register,
    formState: { errors },
    watch,
    setValue,
    trigger,
  } = methods;

  const containerType = watch('containerType');
  const rows = watch('rows');
  const columns = watch('columns');
  const wellAddresses = watch('wellAddresses');
  const amount = watch('amount');
  const units = watch('units');
  const quantityType = watch('quantityType');
  const isEmpty = watch('isEmpty');
  const containerVolumeAmount = watch('containerVolumeAmount');
  const containerVolumeUnits = watch('containerVolumeUnits');
  const wellVolumeAmount = watch('wellVolumeAmount');
  const wellVolumeUnits = watch('wellVolumeUnits');

  const isPlate = containerType === 'Well Plate';

  useEffect(() => {
    void trigger('amount');
  }, [
    trigger,
    amount,
    units,
    quantityType,
    isEmpty,
    containerType,
    containerVolumeAmount,
    containerVolumeUnits,
    rows,
    columns,
    wellVolumeAmount,
    wellVolumeUnits,
  ]);

  const onSubmit = (data: AddMaterialFormValues) => {
    const spec = formToSpec(data, position ?? { x: 100, y: 100 });
    const id = addMaterialNode(spec);

    onSaved?.(id);
    onClose();
  };

  const onInvalid = (
    errors: Partial<
      Record<keyof AddMaterialFormValues, { message?: string }>
    >,
  ) => {
    const amountError = errors.amount?.message;

    if (amountError) {
      toast?.showToast(amountError, 'error');
    }
  };

  return (
    <Dialog
      open
      onClose={onClose}
      maxWidth="md"
      fullWidth
      data-testid="add-material-modal"
      slotProps={{
        paper: {
          sx: {
            width: '878px',
            maxWidth: 'calc(100vw - 32px)',
            height: '573px',
            maxHeight: '90vh',
            display: 'flex',
            flexDirection: 'column',
            borderRadius: '4px',
            overflow: 'hidden',
          },
        },
      }}
    >
      {/* Header */}
      <DialogTitle
        sx={{
          height: '65px',
          minHeight: '65px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: '16px',
          py: 0,
          borderBottom: '1px solid #dee2e6',
          fontSize: '16px',
          fontWeight: 500,
          color: '#1f4f7a',
          backgroundColor: '#f8f9fc',
        }}
      >
        Add Material as Template

        <IconButton
          onClick={onClose}
          size="small"
          aria-label="close"
          sx={{
            color: '#222',
            '&:hover': {
              backgroundColor: 'transparent',
            },
          }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <FormProvider {...methods}>
        <Box
          component="form"
          onSubmit={handleSubmit(onSubmit, onInvalid)}
          noValidate
          sx={{
            display: 'flex',
            flexDirection: 'column',
            flex: 1,
            minHeight: 0,
          }}
        >
          {/* Content */}
          <DialogContent
            sx={{
              px: '32px',
              py: '30px',
              overflowY: 'auto',
              flex: 1,
              minHeight: 0,
              display: 'flex',
              justifyContent: 'center',

              '&::-webkit-scrollbar': {
                width: '8px',
              },

              '&::-webkit-scrollbar-thumb': {
                backgroundColor: '#999',
                borderRadius: '4px',
              },
            }}
          >
            <Box
              sx={{
                maxWidth: '640px',
                mx: 'auto',
              }}
            >
              {/* Material Name */}
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: '170px 1fr',
                  alignItems: 'center',
                  columnGap: '10px',
                  mb: '24px',
                }}
              >
                <Typography
                  sx={{
                    fontSize: '14px',
                    fontWeight: 600,
                    textAlign: 'right',
                  }}
                >
                  Name
                  <Box
                    component="span"
                    sx={{ color: '#dc3545', ml: '2px' }}
                  >
                    *
                  </Box>
                </Typography>

                <TextField
                  {...register('name')}
                  size="small"
                  fullWidth
                  placeholder="Enter Name"
                  error={!!errors.name}
                  helperText={errors.name?.message}
                  autoFocus
                  slotProps={{
                    htmlInput: {
                      'data-testid': 'name-input',
                    },
                  }}
                />
              </Box>

              {/* Quantity */}
              <QuantitySpecForm />

              {/* Container */}
              <Box>
                <ContainerSpecForm />
              </Box>

              {isPlate && rows > 0 && columns > 0 && (
                <Button
                  variant="outlined"
                  onClick={() => setWellDialogOpen(true)}
                  data-testid="choose-well-btn"
                  sx={{ mt: 2 }}
                >
                  Choose Well Address ({wellAddresses.length} selected)
                </Button>
              )}
            </Box>
          </DialogContent>

          {/* Footer */}
          <DialogActions
            sx={{
              height: '76px',
              minHeight: '76px',
              px: '32px',
              borderTop: '1px solid #dee2e6',
              backgroundColor: '#fff',
              justifyContent: 'flex-end',
            }}
          >
            <Button
              type="submit"
              variant="outlined"
              startIcon={<SaveIcon />}
              data-testid="save-btn"
              sx={{
                minWidth: '93px',
                height: '38px',
                textTransform: 'none',
                borderColor: '#1976d2',
                color: '#1976d2',
                fontSize: '14px',
                '&:hover': {
                  borderColor: '#1565c0',
                  backgroundColor: 'rgba(25, 118, 210, 0.04)',
                },
              }}
            >
              Save
            </Button>
          </DialogActions>

          {wellDialogOpen && (
            <ChooseWellAddressDialog
              open
              rows={rows}
              columns={columns}
              selected={wellAddresses}
              onConfirm={(wells) => {
                setValue('wellAddresses', wells);
                setWellDialogOpen(false);
              }}
              onClose={() => setWellDialogOpen(false)}
            />
          )}
        </Box>
      </FormProvider>
    </Dialog>
  );
}